import type { Facing } from '@sokdaq/protocol';
import { isWalkable, type OfficeMap } from './office-map.js';
import type { Point } from './proximity.js';

/**
 * 아바타 이동.
 *
 * 순수 함수다 — 상태도 시간도 밖에서 받는다. 렌더 루프 없이 테스트할 수 있고,
 * 나중에 서버가 같은 규칙으로 검증하고 싶어지면 그대로 가져다 쓰면 된다.
 */

/** 초당 이동 타일 수. 사무실 한 변(40타일)을 10초쯤에 가로지른다. */
export const WALK_SPEED_TILES_PER_SEC = 4;

/**
 * 아바타의 발밑 충돌 상자 반지름(타일).
 *
 * 1보다 작게 잡아야 한 칸짜리 통로를 지나갈 수 있다. 너무 작으면 벽에
 * 몸이 박혀 보인다.
 */
const COLLISION_RADIUS = 0.3;

export interface MoveInput {
  readonly up: boolean;
  readonly down: boolean;
  readonly left: boolean;
  readonly right: boolean;
}

export const NO_INPUT: MoveInput = { up: false, down: false, left: false, right: false };

export interface Actor {
  readonly x: number;
  readonly y: number;
  readonly facing: Facing;
  /** 걷는 중인지. 애니메이션 프레임을 돌릴지 판단한다. */
  readonly moving: boolean;
}

export function hasInput(input: MoveInput): boolean {
  return input.up || input.down || input.left || input.right;
}

/** 두 입력을 합친다. 키보드와 조이스틱이 동시에 있을 수 있다. */
export function mergeInput(a: MoveInput, b: MoveInput): MoveInput {
  return {
    up: a.up || b.up,
    down: a.down || b.down,
    left: a.left || b.left,
    right: a.right || b.right,
  };
}

/**
 * 조이스틱 손가락 위치를 방향 입력으로 바꾼다.
 *
 * 좌표는 스틱 중심 기준으로 반지름을 1로 정규화한 값이다. 아날로그 속도는
 * 쓰지 않는다 — `stepActor`가 8방향 등속 모델이라 여기서 세기를 만들어도
 * 버려진다. 그리고 키보드와 걷는 속도가 달라지면 같은 맵에서 두 조작이
 * 서로 다른 게임처럼 느껴진다.
 */
export const JOYSTICK_DEADZONE = 0.3;

/**
 * 한 축이 켜지는 문턱. sin(22.5°).
 *
 * 원을 45°씩 여덟 조각으로 나눈 경계값이다. 정확히 오른쪽을 밀면 오른쪽만,
 * 대각선 쪽으로 넘어가면 두 축이 함께 켜진다. 문턱을 0으로 두면 미세한
 * 흔들림에도 두 축이 켜져서 대각선으로만 걷게 된다.
 */
const AXIS_THRESHOLD = Math.sin(Math.PI / 8);

export function inputFromVector(dx: number, dy: number, deadzone = JOYSTICK_DEADZONE): MoveInput {
  const magnitude = Math.hypot(dx, dy);
  if (magnitude < deadzone) return NO_INPUT;

  const nx = dx / magnitude;
  const ny = dy / magnitude;

  return {
    left: nx < -AXIS_THRESHOLD,
    right: nx > AXIS_THRESHOLD,
    up: ny < -AXIS_THRESHOLD,
    down: ny > AXIS_THRESHOLD,
  };
}

/**
 * 한 프레임만큼 움직인다.
 *
 * 축을 따로 처리한다. 한 번에 처리하면 벽을 비스듬히 밀 때 완전히 멈춰
 * 버리는데, 축별로 하면 벽을 따라 미끄러진다.
 */
export function stepActor(
  actor: Actor,
  input: MoveInput,
  deltaSeconds: number,
  map: OfficeMap,
): Actor {
  let dx = (input.right ? 1 : 0) - (input.left ? 1 : 0);
  let dy = (input.down ? 1 : 0) - (input.up ? 1 : 0);

  if (dx === 0 && dy === 0) {
    return actor.moving ? { ...actor, moving: false } : actor;
  }

  // 대각선이 더 빠르면 안 된다.
  if (dx !== 0 && dy !== 0) {
    const inverseRoot2 = Math.SQRT1_2;
    dx *= inverseRoot2;
    dy *= inverseRoot2;
  }

  const distance = WALK_SPEED_TILES_PER_SEC * deltaSeconds;
  let x = actor.x;
  let y = actor.y;

  const nextX = x + dx * distance;
  if (canStand(map, nextX, y)) x = nextX;

  const nextY = y + dy * distance;
  if (canStand(map, x, nextY)) y = nextY;

  return {
    x,
    y,
    facing: facingFor(input, actor.facing),
    // 벽에 막혀 한 발도 못 갔으면 걷는 중이 아니다.
    moving: x !== actor.x || y !== actor.y,
  };
}

/**
 * 충돌 상자의 네 모서리가 모두 걸을 수 있는 칸에 있는지 본다.
 *
 * 중심 한 점만 검사하면 몸통이 벽을 파고든다.
 */
function canStand(map: OfficeMap, x: number, y: number): boolean {
  const r = COLLISION_RADIUS;
  return (
    isWalkable(map, x - r, y - r) &&
    isWalkable(map, x + r, y - r) &&
    isWalkable(map, x - r, y + r) &&
    isWalkable(map, x + r, y + r)
  );
}

/**
 * 바라보는 방향.
 *
 * 상하 입력을 좌우보다 앞에 두지 않는다. 좌우로 걸을 때 위아래를 살짝
 * 겹쳐 눌러도 옆모습이 유지되는 편이 자연스럽다.
 */
function facingFor(input: MoveInput, current: Facing): Facing {
  if (input.left) return 'left';
  if (input.right) return 'right';
  if (input.up) return 'up';
  if (input.down) return 'down';
  return current;
}

/**
 * 남의 아바타를 목표 좌표로 부드럽게 당긴다.
 *
 * 서버 프레즌스는 한 틱에 한 번(50ms)만 오므로 그대로 찍으면 뚝뚝 끊긴다.
 * 프레임률에 무관하도록 지수 보간을 쓴다.
 */
export function lerpToward(current: Point, target: Point, deltaSeconds: number, rate = 12): Point {
  const t = 1 - Math.exp(-rate * deltaSeconds);

  // 너무 멀면 따라가지 않고 순간이동한다. 오래 끊겼다 돌아온 사람이
  // 사무실을 가로질러 걸어오는 것처럼 보이면 이상하다.
  if (Math.hypot(target.x - current.x, target.y - current.y) > 6) return target;

  return {
    x: current.x + (target.x - current.x) * t,
    y: current.y + (target.y - current.y) * t,
  };
}
